import { AppState } from 'src/AppState';

import {
	DocumentAddCommandResult,
	EasyClaimDocumentDto,
	ValidationResult,
} from '../easyclaim.api.client.typescript/src/index';

export interface Image {
	/** The unique component id, that is responsible for the life cycle of the image. */
	componentId: string;

	/** The state of the image selection. */
	selectState: SelectState;

	/** In order to add the image as a data url to the img tag, we have to store the head of the data url. */
	dataUrlHead?: string;

	/** The corresponding {@see EasyClaimDocumentDto} for the image. */
	document?: EasyClaimDocumentDto;

	uploadResult?: DocumentAddCommandResult;
}

export enum SelectState {
	Init,
	Reading,
	Read,
	Uploading,
	Uploaded,
	Error,
	UnprocessableEntity,
	UnsupportedMediaType,
}

export enum UploadDestination {
	EasyClaimCase,
	ClaimantRequest,
}

/**
 * This service provides various methods for the SelectImage component.
 */
export class ImageSelectionService {
	public static hasImageSelectionFinished(image?: Image): boolean {
		if (!image?.selectState) return false;

		return [SelectState.Uploaded, SelectState.Read].includes(image.selectState);
	}

	public static hasImageSuccessfullySelectionFinished(image?: Image): boolean {
		if (!image?.selectState) return false;
		if (![SelectState.Uploaded, SelectState.Read].includes(image.selectState)) {
			return false;
		}

		const validationResults =
			image.uploadResult?.validationResults ?? image.document?.validationResults;

		return validationResults
			? validationResults.every((result) => result.validationResult !== ValidationResult.Error)
			: !!image.document?.documentId;
	}

	public static isImageLoading(image?: Image): boolean {
		if (!image?.selectState) return false;

		return [SelectState.Init, SelectState.Reading, SelectState.Uploading, null].includes(
			image.selectState
		);
	}

	public static isEmpty<T>(object: T): boolean {
		return object === null || object === undefined;
	}

	/**
	 * Returns the [data url scheme]{@link https://tools.ietf.org/html/rfc2397} representation for the given image.
	 */
	public static getImageAsDataUrl(image?: Image): string | undefined {
		const fileContent = image?.document?.fileContent;

		if (fileContent?.startsWith('blob:') || fileContent?.startsWith('data:')) {
			return fileContent;
		}

		return image?.dataUrlHead && fileContent ? `${image.dataUrlHead},${fileContent}` : undefined;
	}

	public static imageHasValidationResult(...images: (Image | undefined)[]): boolean {
		return (
			images &&
			images.some(
				(image) => image && image.uploadResult?.documentFeatureValues?.additionalDamageDetected
			)
		);
	}

	public static additionalDamagesDetected(currentAppState: AppState): boolean {
		return ImageSelectionService.imageHasValidationResult(
			currentAppState.vehicleImageRearLeft,
			currentAppState.vehicleImageRearRight,
			currentAppState.vehicleImageFrontLeft,
			currentAppState.vehicleImageFrontRight,
			currentAppState.vehicleImageFront,
			currentAppState.vehicleImageRear,
			currentAppState.vehicleImageLeft,
			currentAppState.vehicleImageRight,
			currentAppState.windshieldImage
		);
	}

	public static isAnyDamageImageLoading(appState: AppState): boolean {
		return (
			ImageSelectionService.isImageLoading(appState.closeUpDamageImage) ||
			ImageSelectionService.isImageLoading(appState.overallDamageImage) ||
			(!!appState.additionalDamageImages &&
				appState.additionalDamageImages.some((i) => ImageSelectionService.isImageLoading(i)))
		);
	}

	public static isAnyDamageImageLoaded(appState: AppState): boolean {
		return (
			ImageSelectionService.hasImageSelectionFinished(appState.closeUpDamageImage) ||
			ImageSelectionService.hasImageSelectionFinished(appState.overallDamageImage) ||
			(!!appState.additionalDamageImages &&
				appState.additionalDamageImages.some((i) =>
					ImageSelectionService.hasImageSelectionFinished(i)
				))
		);
	}
}
